import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  SearchableSelect,
  type SearchableSelectOption,
  type SearchableSelectProps,
} from "@/components/ui/SearchableSelect";
import { apiFetch } from "@/lib/api";
import {
  supplierRowsToSearchableOptions,
  type SupplierForSearchRow,
} from "@/components/mrp/supplierSearchableSelectOptions";

type SupplierSearchableSelectBaseProps = Omit<
  SearchableSelectProps,
  "presentation" | "options" | "isLoading"
>;

export type SupplierSearchableSelectProps = SupplierSearchableSelectBaseProps & {
  /** When set, the search query runs only after the user types at least this many characters. */
  minQueryLength?: number;
  /** Optional static rows (for example preloaded selected supplier). Merged with search results. */
  extraRows?: SupplierForSearchRow[];
  /** Supplier ids to hide from the list (current selection is always kept). */
  excludeValues?: string[];
};

async function fetchSuppliersForPurchaseOrder(search: string): Promise<SupplierForSearchRow[]> {
  const response = await apiFetch<{ rows: SupplierForSearchRow[] }>("/api/mrp/suppliers-for-purchase-order", {
    query: search.trim() !== "" ? { q: search.trim() } : undefined,
  });
  return response.rows;
}

function mergeSupplierRows(
  searchRows: SupplierForSearchRow[],
  extraRows: SupplierForSearchRow[] | undefined,
): SupplierForSearchRow[] {
  const byId = new Map<number, SupplierForSearchRow>();
  for (const row of extraRows ?? []) {
    byId.set(Number(row.id), row);
  }
  for (const row of searchRows) {
    byId.set(Number(row.id), row);
  }
  return [...byId.values()].sort((left, right) =>
    (left.mv_supplier_name ?? "").localeCompare(right.mv_supplier_name ?? "", undefined, {
      sensitivity: "base",
    }),
  );
}

/**
 * Shared Megaventory supplier picker for hub purchase orders, CM PO, RFQ, AP, AML, and forecast.
 * Uses live-only `/api/mrp/suppliers-for-purchase-order` and always `panelSearch` presentation.
 */
export function SupplierSearchableSelect({
  minQueryLength = 0,
  extraRows,
  excludeValues,
  searchValue,
  value,
  placeholder = "Select supplier…",
  searchPlaceholder = "Search by name or id…",
  emptyMessage = "No suppliers match.",
  ...rest
}: SupplierSearchableSelectProps): JSX.Element {
  const trimmedSearch = searchValue.trim();
  const queryEnabled = minQueryLength === 0 || trimmedSearch.length >= minQueryLength;

  const suppliersQuery = useQuery({
    queryKey: ["mrp", "suppliers-for-purchase-order", "widget", minQueryLength, trimmedSearch],
    queryFn: () => fetchSuppliersForPurchaseOrder(trimmedSearch),
    staleTime: 60_000,
    enabled: queryEnabled,
  });

  const options: SearchableSelectOption[] = useMemo(() => {
    if (!queryEnabled) {
      return supplierRowsToSearchableOptions(extraRows ?? []).filter((option) => {
        if (option.value === value) return true;
        return !excludeValues?.includes(option.value);
      });
    }
    const rows = mergeSupplierRows(suppliersQuery.data ?? [], extraRows);
    return supplierRowsToSearchableOptions(rows).filter((option) => {
      if (option.value === value) return true;
      return !excludeValues?.includes(option.value);
    });
  }, [queryEnabled, suppliersQuery.data, extraRows, value, excludeValues]);

  const resolvedEmptyMessage =
    minQueryLength > 0 && trimmedSearch.length < minQueryLength
      ? `Type at least ${minQueryLength} characters to search suppliers.`
      : emptyMessage;

  return (
    <SearchableSelect
      {...rest}
      value={value}
      searchValue={searchValue}
      presentation="panelSearch"
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={resolvedEmptyMessage}
      options={options}
      isLoading={queryEnabled && suppliersQuery.isLoading}
    />
  );
}
